/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { ReactElement } from "react";

import { Redirect, Route } from "react-router-dom";

import { Spinner, SpinnerSize } from "@fluentui/react";

import { useAuth } from "Authentication/AuthProvider";

import { AAD_REPLYURL_SESSIONSTORAGE_KEY } from "../Constants/AuthenticationConstants";

/**
 * Stores the current requested url.
 * Useful in redirecting user after authentication protocols are complete.
 * @method
 */
export const storeCurrentUrl = (): void => {
  sessionStorage.setItem(AAD_REPLYURL_SESSIONSTORAGE_KEY, window.location.pathname);
};

/**
 * Function to store current url and redirect to Login component
 * @function
 */
const renderRedirect = (): ReactElement => {
  storeCurrentUrl();

  return <Redirect to="/login" />;
};

/**
 * Wrapper for creating routes for authenticated components
 * If user is logged in, then the component is rendered, else redirected to Login.
 * @function
 */
const PrivateRoute = ({ component: Component, ...rest }): ReactElement => {
  const { isUserLoggedIn, loading, hasAnyRoles } = useAuth();
  return (
    <Route
      {...rest}
      render={(props): ReactElement => {
        if (!loading) {
          if (!isUserLoggedIn) {
            return renderRedirect();
          } else if (!hasAnyRoles) {
            return <Redirect to="/unauthorized" />;
          } else {
            return <Component {...props} />;
          }
        }

        return <Spinner size={SpinnerSize.large} />;
      }}
    />
  );
};

export default PrivateRoute;
